import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionCard, EmptyState } from "../components/Common";
import { Stethoscope, Search, Circle, ClipboardList, Users } from "lucide-react";
import { DoctorProfileDialog } from "./admitted/DoctorProfileDialog";

const AVAIL = {
  in_clinic: { label: "In clinic", dot: "bg-[#2E7D32]", pill: "bg-emerald-100 text-[#1B5E20]" },
  on_call:   { label: "On call",   dot: "bg-[#B45309]", pill: "bg-amber-100 text-[#B45309]" },
  off:       { label: "Off",       dot: "bg-slate-400", pill: "bg-slate-200 text-slate-600" },
};

export default function Doctors() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/staff/doctor-workload");
      setRows(data || []);
    } catch { toast.error("Failed to load doctors"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const specialties = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => { if (r.specialty) s.add(r.specialty); });
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && (r.specialty || "") !== filter) return false;
      if (!needle) return true;
      return (r.name || "").toLowerCase().includes(needle) || (r.specialty || "").toLowerCase().includes(needle);
    });
  }, [rows, q, filter]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="doctors-directory-page">
      <PageHeader
        title="Doctors directory"
        subtitle="Specialties, live availability, and current workload for every doctor at your facility."
      />

      <SectionCard
        title="All doctors"
        testId="doctors-directory-card"
        actions={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="doctors-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or specialty…"
                className="h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-sm w-56 focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]"
              />
            </div>
            <select
              data-testid="doctors-specialty-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="h-9 px-2.5 rounded-lg border border-slate-200 text-sm bg-white"
            >
              {specialties.map((s) => <option key={s} value={s}>{s === "all" ? "All specialties" : s}</option>)}
            </select>
          </div>
        }
      >
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading doctors…</div>
        ) : visible.length === 0 ? (
          <EmptyState icon={Stethoscope} title="No doctors match your filters" />
        ) : (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {visible.map((d) => {
              const a = AVAIL[d.availability || "in_clinic"] || AVAIL.in_clinic;
              const initials = (d.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
              return (
                <button
                  key={d.user_id}
                  data-testid={`doctor-card-${d.user_id}`}
                  onClick={() => setOpenId(d.user_id)}
                  className="text-left rounded-xl border border-slate-200 bg-white hover:border-[#1976D2]/60 hover:shadow-sm transition p-4 flex items-start gap-3"
                >
                  <div className="relative w-11 h-11 rounded-full bg-[#e6f0fb] text-[#1565C0] flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {initials}
                    <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${a.dot}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-slate-900 truncate">{d.name}</div>
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <Stethoscope className="w-3 h-3" /> {d.specialty || "Unassigned"}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`h-5 px-1.5 rounded-full text-[10px] font-semibold flex items-center gap-1 ${a.pill}`}>
                        <Circle className="w-1.5 h-1.5 fill-current" /> {a.label}
                      </span>
                      <span className="text-[11px] text-slate-500 flex items-center gap-1"><ClipboardList className="w-3 h-3" /> {d.waiting || 0} waiting</span>
                      <span className="text-[11px] text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" /> {d.seen_today || 0} today</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </SectionCard>

      {openId && <DoctorProfileDialog userId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
}
